import { db } from '@/core/db/dexie'
import { syncEndpoints } from '@/core/api/endpoints'
import { ApiError } from '@/core/api/fetchClient'
import type { OutFoto } from '@/core/db/entities'
import type { FotoMetadataDto } from '@/types/dtos'
import { photoStorage } from '@/core/storage'
import { handleFromFoto } from '@/core/storage/handleFromFoto'

/**
 * Push de fotos pendentes. Uma chamada multipart por foto — multipart não bateia
 * bem, e o backend é idempotente, então não precisamos de batch.
 *
 * Ordem: oldest first (capturedAt asc).
 *
 * Drenagem: processa MAX_PER_RUN fotos por ciclo, mas se restou pendente E o
 * último upload foi sucesso (rede ok), agenda novo ciclo na próxima tick. Sem
 * isso, fila de 50 fotos demoraria ~7min pra esvaziar (heartbeat de 60s).
 * Agora esvazia em batch contínuo enquanto a rede aguenta.
 */

const MAX_PER_RUN = 8

let runningPromise: Promise<PushSummary> | null = null

export interface PushSummary {
  attempted: number
  accepted: number
  failed: number
  errors: string[]
}

export async function pushFotoOutbox(): Promise<PushSummary> {
  if (runningPromise) return runningPromise
  runningPromise = doPush().finally(() => {
    runningPromise = null
  })
  return runningPromise
}

async function doPush(): Promise<PushSummary> {
  const summary: PushSummary = { attempted: 0, accepted: 0, failed: 0, errors: [] }

  // Recovery: registros que ficaram presos em SYNCING (tab fechada/recarregada
  // entre marcar SYNCING e a resposta voltar) voltam para PENDING aqui. Sem isso,
  // o filtro abaixo nunca os pegaria e a foto "nunca sincronizaria".
  await db.out_foto
    .where('syncStatus')
    .equals('SYNCING')
    .modify({ syncStatus: 'PENDING' })

  const pending = await db.out_foto
    .where('syncStatus')
    .anyOf('PENDING', 'FAILED')
    .limit(MAX_PER_RUN)
    .toArray()
  if (pending.length === 0) return summary

  summary.attempted = pending.length

  for (const foto of pending) {
    await db.out_foto.update(foto.id, { syncStatus: 'SYNCING' })
    try {
      const metadata = buildMetadata(foto)
      // Lê o blob via PhotoStorage — em web vem do fileBlob, em native vem do
      // arquivo no Filesystem. Sync engine não precisa saber a diferença.
      const handle = handleFromFoto(foto)
      if (!handle) {
        throw new Error('Foto sem fileBlob nem localPath — registro corrompido')
      }
      const blob = await photoStorage().read(handle)
      const res = await syncEndpoints.foto(metadata, blob)

      // Proteção secundária contra o bug de fotos órfãs:
      // backend retorna `blobVerified` quando confirmou o blob no bucket.
      // Resposta sem `blobVerified` (backend antigo) ou explicitamente false
      // = não dá pra confiar que o upload foi pra frente. Marca FAILED pra
      // retentar no próximo ciclo (idempotência do backend cuida).
      if (res.blobVerified === false) {
        throw new Error('Resposta inconsistente do servidor — blob não verificado')
      }

      const now = new Date().toISOString()
      await db.out_foto.update(foto.id, {
        syncStatus: 'SYNCED',
        uploadedAt: res.uploadedAt ?? now,
        // Storage key veio do backend (antes era `res.minioKey`); preservamos o nome
        // do campo no IndexedDB pra não exigir migração de schema Dexie.
        minioKey: res.storageKey,
        failureReason: null,
        lastSyncAttempt: Date.now(),
      })
      summary.accepted += 1
    } catch (err) {
      const reason =
        err instanceof ApiError ? `${err.status}: ${err.message}` : (err as Error).message
      const isNetwork = err instanceof ApiError && err.isOffline
      await db.out_foto.update(foto.id, {
        syncStatus: isNetwork ? 'PENDING' : 'FAILED',
        failureReason: reason,
        lastSyncAttempt: Date.now(),
        retryCount: (foto.retryCount ?? 0) + 1,
      })
      summary.failed += 1
      summary.errors.push(reason)
      // Falha de rede: aborta o loop — provavelmente todas falharão
      if (isNetwork) break
    }
  }

  // Drenagem contínua: se chegamos no MAX_PER_RUN com aceitos > 0 (rede ok)
  // e ainda há pendentes na fila, agenda novo ciclo imediatamente em vez de
  // esperar o heartbeat de 60s. Mantém UI responsiva pra esvaziar fila grande
  // (cenário típico: operador volta de obra com 50 fotos offline acumuladas).
  if (summary.accepted === MAX_PER_RUN) {
    const remaining = await db.out_foto
      .where('syncStatus')
      .anyOf('PENDING', 'FAILED')
      .count()
    if (remaining > 0) {
      // Setimeout 0 sai do call stack atual (libera runningPromise pra resetar)
      // antes do próximo pushFotoOutbox tentar ganhar a corrida.
      setTimeout(() => {
        void pushFotoOutbox()
      }, 0)
    }
  }

  return summary
}

function buildMetadata(f: OutFoto): FotoMetadataDto {
  return {
    id: f.id,
    controleProducaoId: f.controleProducaoId,
    carreteiroId: f.carreteiroId,
    projetoId: f.projetoId,
    projetoCodigo: f.projetoCodigo,
    servicoExecutadoId: f.servicoExecutadoId,
    servicoExecutadoNome: f.servicoExecutadoNome,
    encarregadoId: f.encarregadoId,
    encarregadoNome: f.encarregadoNome,
    equipeId: f.equipeId,
    equipeNome: f.equipeNome,
    lat: f.lat,
    lng: f.lng,
    accuracy: f.accuracy,
    capturedAt: f.capturedAt,
    mime: f.mime,
    sizeBytes: f.sizeBytes,
    width: f.width,
    height: f.height,
    sha256: f.sha256 ?? undefined,
    obs: f.obs,
  }
}
